/**
 * Audit log viewer for the admin panel
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Pagination,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { History as HistoryIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useAdmin } from '../../contexts/AdminContext';
import { AuditLog, AuditLogFilters } from '../../types/admin';
import { formatDate } from '../../utils/formatters';

const AuditLogs: React.FC = () => {
  const {
    auditLogs,
    auditLogTotal,
    auditLogsLoading,
    auditLogsError,
    loadAuditLogs,
    clearError,
  } = useAdmin();

  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    page_size: 25,
  });
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  useEffect(() => {
    loadAuditLogs({
      ...filters,
      action: actionFilter || undefined,
      resource_type: resourceFilter || undefined,
    });
  }, [loadAuditLogs, filters, actionFilter, resourceFilter]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadAuditLogs({
        ...filters,
        action: actionFilter || undefined,
        resource_type: resourceFilter || undefined,
      });
    } finally {
      setRefreshing(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(auditLogTotal / (filters.page_size ?? 25)));

  if (auditLogsLoading && auditLogs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" display="flex" alignItems="center" gap={1}>
          <HistoryIcon />
          Audit Logs
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} disabled={refreshing} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {auditLogsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {auditLogsError}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Filter by action"
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setFilters((f) => ({ ...f, page: 1 }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Filter by resource type"
                value={resourceFilter}
                onChange={(e) => {
                  setResourceFilter(e.target.value);
                  setFilters((f) => ({ ...f, page: 1 }));
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Resource</TableCell>
              <TableCell>User</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auditLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No audit log entries found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              auditLogs.map((log) => (
                <TableRow
                  key={String(log.id)}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedLog(log)}
                >
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {log.action.replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell>
                    {log.resource_type}
                    {log.resource_id ? ` #${log.resource_id}` : ''}
                  </TableCell>
                  <TableCell>{log.user_id ?? log.admin_id ?? '—'}</TableCell>
                  <TableCell>{log.ip_address || '—'}</TableCell>
                  <TableCell>{formatDate(log.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {auditLogTotal > (filters.page_size ?? 25) && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={pageCount}
            page={filters.page ?? 1}
            onChange={(_, page) => setFilters((f) => ({ ...f, page }))}
            color="primary"
          />
        </Box>
      )}

      <Dialog open={!!selectedLog} onClose={() => setSelectedLog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Audit Log Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
              {JSON.stringify(selectedLog.details ?? selectedLog, null, 2)}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;
